/**
 * Granular, lazy reconcile of the editor's files into the Knowledge Graph.
 *
 * Pull-diff: flatten the current systems, compare each file's content to what
 * we last pushed, and call the KG ONLY for files that were added/changed/removed.
 * Unchanged files are skipped entirely — no re-index, no cascade. The existing
 * incremental KG cascade (which touches only dependent nodes) is reused as-is.
 * Triggered at coarse checkpoints (see SandboxEditorReact), never per keystroke.
 */
import { buildVirtualFiles, type SandboxSystemsInput } from '@gotti/sandbox-core';
import { onFileChanged, onFileRemoved } from '@gotti/sandbox-ai-v2';
import type { SandboxSystems } from '../editor/types';

const lastSyncedContent = new Map<string, string>();

export function syncKnowledgeGraphFiles(systems: SandboxSystems): void {
  const live = new Map<string, string>();
  for (const [slashPath, content] of buildVirtualFiles(systems as unknown as SandboxSystemsInput)) {
    live.set(slashPath.replace(/^\/+/, ''), content); // '/client/..' -> 'client/..'
  }
  for (const [path, content] of live) {
    if (lastSyncedContent.get(path) !== content) {
      onFileChanged(path, content);
      lastSyncedContent.set(path, content);
    }
  }
  for (const path of [...lastSyncedContent.keys()]) {
    if (!live.has(path)) {
      onFileRemoved(path);
      lastSyncedContent.delete(path);
    }
  }
}

/** Keep the mirror coherent with the AI write path (which calls onFileChanged directly). */
export function noteSyncedFile(path: string, content: string): void {
  lastSyncedContent.set(path, content);
}

/** Test/reset hook so the mirror doesn't leak across runs. */
export function _resetFileSyncMirror(): void {
  lastSyncedContent.clear();
}
